import { Dispatch, SetStateAction, useState } from 'react';
import { compareAsc, compareDesc, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ArrowUpWideNarrow, Loader2 } from 'lucide-react';
import { useMediaPredicate } from 'react-media-hook';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { ScrollArea } from 'components/ui/scroll-area';
import { Button } from 'components/ui/button';
import { cn, getDateTime } from 'lib/utils';
import { HealthReadingData } from 'services/face-scan/type';

interface PreviousScansProps {
  isLoading?: boolean;
  readings: HealthReadingData[];
  selectedReadingId: string;
  scrollViewElement: React.ReactNode;
  setReadings: Dispatch<SetStateAction<HealthReadingData[]>>;
  setSelectedReadingId: Dispatch<SetStateAction<string>>;
}

export default function PreviousScans({
  isLoading,
  readings,
  selectedReadingId,
  scrollViewElement,
  setReadings,
  setSelectedReadingId,
}: PreviousScansProps) {
  const { t } = useTranslation();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const [sort, setSort] = useState<'Ascending' | 'Descending'>('Descending');

  const onReadingsSort = () => {
    if (!isMediaMobile && !selectedReadingId) return;
    const newSort = sort === 'Ascending' ? 'Descending' : 'Ascending';
    const sortedData =
      newSort === 'Ascending'
        ? readings.sort((a, b) =>
            compareAsc(parseISO(a.created_at), parseISO(b.created_at))
          )
        : readings.sort((a, b) =>
            compareDesc(parseISO(a.created_at), parseISO(b.created_at))
          );

    setSort(newSort);
    setReadings(sortedData);
  };

  return (
    <div>
      <h4 className='mb-2 font-semibold text-center uppercase'>
        {t('previous_scans')}
      </h4>

      <div id='sort-share'>
        <Button
          variant='outline'
          className='text-sm font-semibold rounded-full h-7 border-secondary text-secondary hover:bg-secondary/5 hover:text-secondary/90'
          size='sm'
          onClick={onReadingsSort}
        >
          <ArrowUpWideNarrow className='w-4 h-4 mb-1 mr-2' />
          {t('sort')}: {sort === 'Ascending' ? t('ascending') : t('descending')}
        </Button>
      </div>

      <div
        className='w-full mt-4 border rounded-lg border-secondary'
        id='previous-scan-table'
      >
        <ScrollArea
          className={cn(
            'h-[calc(100vh-10rem)] sm:h-[calc(100vh-18rem)] lg:h-[calc(100vh-20rem)]',
            isLoading && 'grid place-content-center'
          )}
        >
          {isLoading ? (
            <Loader2
              role='progressbar'
              className='stroke-1 text-primary animate-spin size-6'
            />
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow className='border-b-2 border-primary'>
                    <TableHead className='h-10 mt-1 text-base font-semibold text-secondary'>
                      {t('timestamp')}
                    </TableHead>
                    <TableHead className='h-10 mt-1 text-base font-semibold text-secondary'>
                      {t('score')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {readings?.map((scan, index) => {
                    const { date, time } = getDateTime(scan?.created_at);
                    return (
                      <TableRow
                        className={cn(
                          'cursor-pointer hover:bg-secondary/5',
                          selectedReadingId === scan.reading_id &&
                            'bg-secondary/10'
                        )}
                        key={index}
                        onClick={() => setSelectedReadingId(scan.reading_id)}
                      >
                        <TableCell className='py-2 font-medium'>
                          <p>{date}</p>
                          <p className='text-primary'>{time}</p>
                        </TableCell>
                        <TableCell className='py-2 font-medium'>
                          {scan?.WELLNESS_INDEX}/100
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {scrollViewElement}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
